import {
    getTestDefinition,
    listTestDefinitions,
} from './queries';
import {
    createTestDefinition,
    deleteTestDefinition,
    setTestDefinitionStatus,
    updateTestDefinition,
} from './mutations';

import {GraphQLQuery} from '@aws-amplify/api';

import {
    CreateTestDefinition,
    DeleteTestDefinition,
    GetTestDefinition,
    ListTestDefinitions,
    SetTestDefinitionStatus,
    TestDefinitionFormValues,
    TestDefinitionStatus,
    TestStep,
    UpdateTestDefinition,
} from '../types.ts';
import {getTestingApiClient} from '../../testing-api-integration/client.ts';

/**
 * Normalises the authored steps into TestStepInput shape.
 *
 * ARRAY ORDER IS THE SOURCE OF TRUTH. The form reorders by moving entries within
 * the array; `stepNumber` is then re-derived here, contiguously from 1. That is
 * what makes a move-up/move-down (or a delete from the middle) persist correctly
 * rather than leaving a gap, a duplicate, or an order that disagrees with what
 * the user saw.
 *
 * Also strips the `__typename` AppSync returns on nested objects — sending it
 * back inside TestStepInput / StepInputInput would be rejected as an unknown
 * field. (Same trap as WeightedActionCodeInput in testing-personas.)
 */
export const toStepsInput = (steps?: TestStep[] | null): TestStep[] =>
    (steps ?? [])
        // A step with no actionCode is an empty row the user added and never
        // filled in; actionCode is NonNull on TestStepInput so it cannot be sent.
        .filter(step => (step.actionCode ?? '').trim() !== '')
        .map((step, index) => ({
            stepNumber: index + 1,
            actionCode: step.actionCode.trim(),
            // Blank key/value rows are dropped for the same reason — both are
            // NonNull on StepInputInput.
            inputs: (step.inputs ?? [])
                .filter(entry => (entry.key ?? '').trim() !== '')
                .map(entry => ({
                    key: entry.key.trim(),
                    value: entry.value ?? '',
                })),
            expect: step.expect,
        }));

export const fetchRecords = async (filter: object) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<ListTestDefinitions>>({
        query: listTestDefinitions,
        variables: {filter: filter},
    });
    return response.data?.listTestDefinitions.items ?? [];
};

export const fetch = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<GetTestDefinition>>({
        query: getTestDefinition,
        variables: {id: id},
    });
    return response.data?.getTestDefinition;
};

export const add = async (payload: TestDefinitionFormValues) => {
    // Whitelist the CreateTestDefinitionInput fields — never pass the whole
    // record through. `status` is NonNull with no server default: omitting it
    // fails with "coerced Null value for NonNull type 'TestDefinitionStatus!'".
    const input = {
        testCode: payload.testCode,
        name: payload.name,
        description: payload.description || undefined,
        // `applicationId: ID` is NULLABLE (FNP-976). An empty form value is sent
        // as null rather than '' — an empty string is a real (dangling) id as
        // far as the store is concerned, whereas null means "no application".
        applicationId: payload.applicationId || null,
        steps: toStepsInput(payload.steps),
        status: payload.status,
    };

    const response = await getTestingApiClient().graphql<GraphQLQuery<CreateTestDefinition>>({
        query: createTestDefinition,
        variables: {input: input},
    });
    return response.data?.createTestDefinition;
};

export const update = async (id: string, record: TestDefinitionFormValues) => {
    // updateTestDefinition SETs every field unconditionally, so all of them are
    // always sent — a partial payload would blank the omitted ones. `status` is
    // carried through unchanged; the edit form does not expose it and
    // transitions go through setStatus() below.
    const input = {
        id: id,
        testCode: record.testCode,
        name: record.name,
        description: record.description || undefined,
        applicationId: record.applicationId || null,
        steps: toStepsInput(record.steps),
        status: record.status,
    };

    const response = await getTestingApiClient().graphql<GraphQLQuery<UpdateTestDefinition>>({
        query: updateTestDefinition,
        variables: {input: input},
    });
    return response.data?.updateTestDefinition;
};

export const deleteRecord = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<DeleteTestDefinition>>({
        query: deleteTestDefinition,
        variables: {input: {id: id}},
    });
    return response.data?.deleteTestDefinition;
};

export const setStatus = async (id: string, targetStatus: TestDefinitionStatus) => {
    // Direct args, NOT an input object.
    const response = await getTestingApiClient().graphql<GraphQLQuery<SetTestDefinitionStatus>>({
        query: setTestDefinitionStatus,
        variables: {
            id: id,
            targetStatus: targetStatus,
        },
    });
    return response.data?.setTestDefinitionStatus;
};
